import { useState, useEffect, Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { HiOutlineSelector } from "react-icons/hi";
import Cookies from "js-cookie";
import { useTranslation } from "next-i18next";
import { IoLocationSharp } from "react-icons/io5";
import { useRouter } from "next/router";
import { useRegionQuery } from "@framework/common/get-region";
import RegionSwitcherSkeleton from "@components/ui-old/loaders/region-switcher-loader";
import { setSiteCookie } from "@framework/utils/set-cookies";
export type RegionOption = {
  pk: number;
  region: string;
};
export default function RegionSwitcher() {
  const {
    t
  } = useTranslation("common");
  const router = useRouter();
  const {
    data: regions = [],
    isLoading,
    error
  } = useRegionQuery();
  const [selectedItem, setSelectedItem] = useState<RegionOption | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (regions.length > 0) {
      const initialRegionId = Cookies.get("choosedKzRegionid");
      let initialOption: RegionOption | undefined;
      if (initialRegionId) {
        initialOption = regions.find(o => o.pk.toString() === initialRegionId);
      }
      if (!initialOption && regions.length > 0) {
        initialOption = regions[0];
        setSiteCookie("choosedKzRegionid", regions[0].pk.toString());
      }
      setSelectedItem(initialOption || null);
      setIsInitialized(true);
    }
  }, [regions]);
  function handleItemClick(values: RegionOption) {
    setSelectedItem(values);
    setSiteCookie("choosedKzRegionid", values.pk.toString());
    router.reload();
  }
  if (!isInitialized || isLoading) {
    return <RegionSwitcherSkeleton />;
  }
  if (error) {
    return <p>{t("text-error")}</p>;
  }
  return <Listbox value={selectedItem} onChange={handleItemClick} data-sentry-element="Listbox" data-sentry-component="RegionSwitcher" data-sentry-source-file="region-switcher.tsx">
      {({
      open
    }) => <div className="relative z-10 w-[140px] sm:w-[150px] lg:w-[130px] xl:w-[150px] ml-2">
          <Listbox.Button className="
              border border-neutral-300 text-heading text-[13px] xl:text-sm 
              font-semibold relative w-full py-2 ltr:pl-3 rtl:pr-3 
              ltr:pr-7 rtl:pl-7 ltr:text-left rtl:text-right 
              bg-white dark:bg-zinc-950 rounded-lg shadow-md 
              focus:outline-none focus-visible:ring-2 
              focus-visible:ring-opacity-75 focus-visible:ring-white 
              focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 
              focus-visible:border-indigo-500 cursor-pointer
            ">
            <span className="flex truncate items-center">
              <span className="ltr:mr-1.5 rtl:ml-1.5">
                <IoLocationSharp />
              </span>
              {selectedItem ? t(selectedItem.region) : t("select-region")}
            </span>
            <span className="absolute inset-y-0 ltr:right-0 rtl:left-0 flex items-center ltr:pr-1.5 rtl:pl-1.5 pointer-events-none">
              <HiOutlineSelector className="w-5 h-5 text-neutral-400" />
            </span>
          </Listbox.Button>

          <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options static className="
                absolute w-full py-1 mt-1 overflow-auto bg-white dark:bg-zinc-950 rounded-md 
                shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 
                focus:outline-none text-sm
              ">
              {regions.map(option => <Listbox.Option key={option.pk} className={({
            active
          }) => `${active ? "text-neutral-100 bg-neutral-900" : "text-neutral-900"} 
                    cursor-pointer select-none relative py-2 px-3 rounded-xl shadow-md`} value={option}>
                  {({
              selected,
              active
            }) => <span className="flex items-center">
                      <IoLocationSharp />
                      <span className={`${selected ? "font-medium" : "font-normal"} block truncate ltr:ml-1.5 rtl:mr-1.5`}>
                        {t(option.region)}
                      </span>
                      {selected ? <span className={`${active && "text-neutral-600"} absolute inset-y-0 ltr:left-0 rtl:right-0 flex items-center ltr:pl-3 rtl:pr-3`} /> : null}
                    </span>}
                </Listbox.Option>)}
            </Listbox.Options>
          </Transition>
        </div>}
    </Listbox>;
}